import { useMemo, useState } from "react";
import { CalendarBlank, CaretDown, Clock, X } from "@phosphor-icons/react";
import DatePicker from "./DatePicker";
import DateRangePicker from "./DateRangePicker";
import TimeSlotPicker from "./TimeSlotPicker";
import { formatDateForDisplay } from "@shared/datetime/dateUtils";
import { formatTimeForDisplay } from "@shared/datetime/timeSlotUtils";

function displayValue({ mode, value, startDate, endDate, placeholder }) {
  if (mode === "date-range") {
    if (startDate || endDate) return `${formatDateForDisplay(startDate) || "Start"} - ${formatDateForDisplay(endDate) || "End"}`;
    return placeholder;
  }
  if (mode === "date") return formatDateForDisplay(value) || placeholder;
  if (mode === "time") {
    if (Array.isArray(value)) return value.map(formatTimeForDisplay).filter(Boolean).join(", ") || placeholder;
    return formatTimeForDisplay(value) || placeholder;
  }
  if (value?.date || value?.time) return `${formatDateForDisplay(value?.date) || "Date"} ${formatTimeForDisplay(value?.time) || ""}`.trim();
  return placeholder;
}

export default function DateTimeField({
  label,
  mode = "date",
  value,
  onChange,
  startDate,
  endDate,
  onRangeChange,
  placeholder = "Select",
  minDate,
  maxDate,
  disabledWeekdays,
  disabledDates,
  timePickerMode = "freeform",
  slots,
  bookedSlots,
  multiple = false,
  startTime,
  endTime,
  intervalMinutes,
  className = "",
  ...triggerProps
}) {
  const [open, setOpen] = useState(false);
  const shownValue = useMemo(
    () => displayValue({ mode, value, startDate, endDate, placeholder }),
    [mode, value, startDate, endDate, placeholder],
  );

  const updateDateTime = (key, nextValue) => {
    onChange?.({ ...(value || {}), [key]: nextValue });
  };

  return (
    <div className={className}>
      {label ? <label className="mb-2 block text-sm font-bold text-[#224032]">{label}</label> : null}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex min-h-[52px] w-full items-center justify-between rounded-2xl border border-[#DED7CC] bg-white px-4 py-3 text-left text-sm font-semibold text-[#224032] shadow-sm transition hover:border-[#224032]"
        {...triggerProps}
      >
        <span className={shownValue === placeholder ? "text-[#64756D]" : ""}>{shownValue}</span>
        <span className="inline-flex items-center gap-2 text-[#64756D]">
          {mode === "time" ? <Clock size={18} weight="duotone" /> : <CalendarBlank size={18} weight="duotone" />}
          <CaretDown size={14} weight="bold" />
        </span>
      </button>

      {open ? (
        <div className="fixed inset-0 z-[1000] flex items-end justify-center bg-[#143124]/35 px-3 py-4 backdrop-blur-sm sm:items-center">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-[#FDFBF7] p-4 shadow-2xl sm:p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 className="text-lg font-extrabold text-[#224032]">{label || "Select date and time"}</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#DED7CC] bg-white text-[#224032]"
                aria-label="Close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            {mode === "date" ? (
              <DatePicker
                selectedDate={value}
                onSelect={(dateKey) => {
                  onChange?.(dateKey);
                  setOpen(false);
                }}
                minDate={minDate}
                maxDate={maxDate}
                disabledWeekdays={disabledWeekdays}
                disabledDates={disabledDates}
              />
            ) : null}

            {mode === "time" ? (
              <TimeSlotPicker
                mode={timePickerMode}
                value={value}
                onChange={onChange}
                slots={slots}
                bookedSlots={bookedSlots}
                multiple={multiple}
                startTime={startTime}
                endTime={endTime}
                intervalMinutes={intervalMinutes}
              />
            ) : null}

            {mode === "datetime" ? (
              <div className="grid gap-4 lg:grid-cols-[1fr_0.8fr]">
                <DatePicker
                  selectedDate={value?.date}
                  onSelect={(dateKey) => updateDateTime("date", dateKey)}
                  minDate={minDate}
                  maxDate={maxDate}
                  disabledWeekdays={disabledWeekdays}
                  disabledDates={disabledDates}
                />
                <TimeSlotPicker
                  mode={timePickerMode}
                  value={value?.time}
                  onChange={(time) => updateDateTime("time", time)}
                  slots={slots}
                  bookedSlots={bookedSlots}
                  date={value?.date}
                  startTime={startTime}
                  endTime={endTime}
                  intervalMinutes={intervalMinutes}
                />
              </div>
            ) : null}

            {mode === "date-range" ? (
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={onRangeChange}
                minDate={minDate}
                maxDate={maxDate}
                disabledWeekdays={disabledWeekdays}
                disabledDates={disabledDates}
              />
            ) : null}

            {mode !== "date" ? (
              <div className="mt-5 flex justify-end">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded-2xl bg-[#224032] px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#1B3428]"
                >
                  Done
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
